import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';
const Ctx = createContext(null);
export function EditorProvider(props) {
    return _jsx(Ctx.Provider, { value: props.value, children: props.children });
}
/** The editor handle (throws outside `EditorProvider`). */
export function useEditor() {
    const h = useContext(Ctx);
    if (!h)
        throw new Error('useEditor() must be used inside <EditorProvider> or <DotloomEditor>');
    return h;
}
/** Select a slice of the editor state; re-renders only when it changes. */
export function useEditorState(select) {
    const { core } = useEditor();
    return useSyncExternalStore(core.state.subscribe, () => select(core.state.getSnapshot()), () => select(core.state.getSnapshot()));
}
export function usePointer() {
    const { core } = useEditor();
    return useSyncExternalStore(core.pointer.subscribe, core.pointer.getSnapshot, core.pointer.getSnapshot);
}
export function useAutosaveState() {
    const { autosave } = useEditor();
    const sub = autosave ? autosave.state.subscribe : () => () => undefined;
    const get = autosave ? autosave.state.getSnapshot : () => null;
    return useSyncExternalStore(sub, get, get);
}
/** Fetch something whenever the committed revision changes (latest result wins). */
export function useRevisionQuery(fetch, deps = []) {
    const { engine } = useEditor();
    const revision = useEditorState((s) => s.revision);
    const [value, setValue] = useState(null);
    // biome-ignore lint/correctness/useExhaustiveDependencies: revision and caller deps drive refetching
    useEffect(() => {
        let live = true;
        fetch(engine).then((v) => {
            if (live)
                setValue(v);
        }, () => {
            if (live)
                setValue(null);
        });
        return () => {
            live = false;
        };
    }, [engine, revision, ...deps]);
    return value;
}
/** The document (refetched per commit; previews do not trigger it). */
export function useDocument() {
    return useRevisionQuery((e) => e.documentJson());
}
export function useEntityInfo(id) {
    return useRevisionQuery((e) => (id === null ? Promise.resolve(null) : e.entityInfo(id)), [id]);
}
export function useAnalysis() {
    return useRevisionQuery((e) => e.analyze());
}
/** Registered plugin type definitions (for labels and dimensions of properties). */
export function usePluginTypes() {
    const { plugins, engine } = useEditor();
    const [types, setTypes] = useState(new Map());
    useEffect(() => {
        let live = true;
        const load = () => {
            engine.pluginTypes().then((list) => {
                if (live)
                    setTypes(new Map(list.map((t) => [t.typeId, t.definition])));
            }, () => undefined);
        };
        load();
        const off = plugins.subscribe(load);
        return () => {
            live = false;
            off();
        };
    }, [engine, plugins]);
    return types;
}
//# sourceMappingURL=context.js.map